import { Info } from "lucide-react";
import type React from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@krea/shared/design-system/Button";
import { Input } from "@krea/shared/design-system/inputs/Input";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";

import { BANKID_TEST_IDS } from "../../test-constants";

interface SSNAuthFlowProps {
  ssn: string;
  onSSNChange: (ssn: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const SSNAuthFlow: React.FC<SSNAuthFlowProps> = ({
  ssn,
  onSSNChange,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation();

  const onChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const digitsAndHyphenValue = value.replace(/[^0-9-]/g, "");
    onSSNChange(digitsAndHyphenValue);
  };

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    onSubmit();
  };

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <Input
        size="lg"
        type="text"
        pattern="[0-9]*"
        inputMode="numeric"
        value={ssn}
        onChange={onChange}
        placeholder="ÅÅÅÅMMDD-XXXX"
        name="bankid-ssn"
        id="bankid-ssn"
        data-storage="false"
        autoComplete="off"
        data-test-id={BANKID_TEST_IDS.ssnInput}
        label={
          <div className="flex items-center text-black">
            <span className="mr-1">{t("bankID.SE.ssn")}</span>
            <ResponsiveTooltip
              className="max-w-[300px]"
              trigger={
                <Info size={18} className="fill-black-100 text-white" />
              }
            >
              {t("bankID.SE.ssnHelper")}
            </ResponsiveTooltip>
          </div>
        }
      />

      <Button
        size="lg"
        variant={!ssn ? "secondary" : "default"}
        type="submit"
        className="mt-5 w-full"
        disabled={isSubmitting || !ssn}
        data-test-id={BANKID_TEST_IDS.bankIdContinueButton}
      >
        {t("bankID.common.continue")}
      </Button>
    </form>
  );
};
